import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format, subDays, parseISO, differenceInDays } from "date-fns";

export interface MetricGap {
  metricKey: string;
  metricLabel: string;
  metricEmoji: string;
  /** Dates with no data and not yet marked as intentional no-data */
  gapDates: string[]; // YYYY-MM-DD
}

/**
 * Maximum number of days back to look for gaps.
 * Prevents scanning the entire history for long-inactive users.
 */
const MAX_GAP_LOOKBACK_DAYS = 14;

/**
 * Minimum number of logged days required before showing the backfill prompt.
 * Prevents showing the prompt to brand-new users.
 */
const MIN_LOGGED_DAYS = 2;

/**
 * Standard metric key → label/emoji mapping.
 * Custom metrics are handled separately via the custom_metrics table.
 */
const STANDARD_METRICS: Record<string, { label: string; emoji: string }> = {
  calories: { label: "Calories", emoji: "🔥" },
  protein: { label: "Protein", emoji: "🍗" },
  carbs: { label: "Carbs", emoji: "🍞" },
  fiber: { label: "Fiber", emoji: "🌾" },
  added_sugars: { label: "Added Sugars", emoji: "🍬" },
  sleep: { label: "Sleep", emoji: "😴" },
  water_gallons: { label: "Water", emoji: "💧" },
  gym_time: { label: "Gym Time", emoji: "💪" },
  miles_ran: { label: "Run Distance", emoji: "🏃" },
  body_weight: { label: "Weight", emoji: "⚖️" },
  body_fat_percentage: { label: "Body Fat", emoji: "📊" },
  mood: { label: "Mood", emoji: "😊" },
  self_appearance_rating: { label: "Self Appearance", emoji: "✨" },
  ate_healthy: { label: "Ate Healthy", emoji: "🥗" },
  low_sugars: { label: "Low Sugars", emoji: "🚫" },
  fasted: { label: "Fasted", emoji: "🍽️" },
  github_commits: { label: "GitHub Commits", emoji: "💻" },
  healthkit_steps: { label: "Steps", emoji: "👟" },
  healthkit_exercise_time: { label: "Exercise", emoji: "🏋️" },
  healthkit_sleep_time: { label: "Sleep Time", emoji: "😴" },
  healthkit_stand_hours: { label: "Stand Hours", emoji: "🚶" },
  healthkit_active_energy: { label: "Active Energy", emoji: "🏃‍♂️" },
};

/**
 * Maps metric_preferences keys to the daily_entries column name(s).
 * Most map 1:1. Custom metrics are in the format "custom_N" in preferences
 * but "custom_N_value" in daily_entries.
 */
function getEntryColumn(metricKey: string): string {
  if (metricKey.startsWith("custom_") && !metricKey.endsWith("_value")) {
    return `${metricKey}_value`;
  }
  return metricKey;
}

/**
 * Hook that detects gaps in metric logging and returns which metrics have
 * unlogged days between their last entry and yesterday.
 * 
 * Returns null if the user shouldn't be prompted (new user, first day, no gaps).
 */
export function useBackfillCheck(userId: string | undefined) {
  return useQuery({
    queryKey: ["backfill-check", userId],
    queryFn: async (): Promise<MetricGap[] | null> => {
      if (!userId) return null;

      const today = format(new Date(), "yyyy-MM-dd");
      const yesterday = format(subDays(new Date(), 1), "yyyy-MM-dd");
      const lookbackStart = format(subDays(new Date(), MAX_GAP_LOOKBACK_DAYS), "yyyy-MM-dd");

      // 1. Get enabled metrics
      const { data: prefs } = await supabase
        .from("metric_preferences")
        .select("nutrition_metrics, activity_sleep_metrics, results_metrics")
        .eq("user_id", userId)
        .maybeSingle();

      if (!prefs) return null;

      const enabledKeys = [
        ...(prefs.nutrition_metrics || []),
        ...(prefs.activity_sleep_metrics || []),
        ...(prefs.results_metrics || []),
      ];

      if (enabledKeys.length === 0) return null;

      // 2. Get custom metric definitions for labels/emojis
      const { data: customMetrics } = await supabase
        .from("custom_metrics")
        .select("metric_key, metric_name, metric_emoji")
        .eq("user_id", userId);

      const customMetricMap = new Map<string, { label: string; emoji: string }>();
      for (const cm of customMetrics || []) {
        customMetricMap.set(cm.metric_key, {
          label: cm.metric_name,
          emoji: cm.metric_emoji || "📊",
        });
      }

      // 3. Get daily entries for the lookback window
      //    We need to select all enabled metric columns
      const columns = ["entry_date", ...enabledKeys.map(getEntryColumn)];
      const { data: entries } = await supabase
        .from("daily_entries")
        .select(columns.join(", "))
        .eq("user_id", userId)
        .gte("entry_date", lookbackStart)
        .lte("entry_date", yesterday)
        .order("entry_date", { ascending: true });

      if (!entries || entries.length === 0) {
        // Check if user has EVER logged anything (don't prompt brand new users)
        const { count } = await supabase
          .from("daily_entries")
          .select("id", { count: "exact", head: true })
          .eq("user_id", userId);

        if (!count || count < MIN_LOGGED_DAYS) return null;
      }

      // 4. Also check entries before the lookback window to see if user has enough history
      const { count: totalEntryCount } = await supabase
        .from("daily_entries")
        .select("id", { count: "exact", head: true })
        .eq("user_id", userId);

      if (!totalEntryCount || totalEntryCount < MIN_LOGGED_DAYS) return null;

      // 5. Get already-marked no_data_entries for this window
      const { data: noDataEntries } = await supabase
        .from("no_data_entries" as any)
        .select("entry_date, metric_key")
        .eq("user_id", userId)
        .gte("entry_date", lookbackStart)
        .lte("entry_date", yesterday);

      const noDataSet = new Set<string>();
      for (const nd of noDataEntries || []) {
        noDataSet.add(`${nd.entry_date}_${nd.metric_key}`);
      }

      // 6. Build entry map by date
      const entryMap = new Map<string, any>();
      for (const entry of entries || []) {
        entryMap.set(entry.entry_date, entry);
      }

      // 7. For each metric, find the last logged date and detect gaps
      const gaps: MetricGap[] = [];

      for (const metricKey of enabledKeys) {
        const column = getEntryColumn(metricKey);

        // Find the last date this metric was logged (most recent non-null value)
        let lastLoggedDate: string | null = null;
        // Search backwards from yesterday
        for (let i = 0; i <= MAX_GAP_LOOKBACK_DAYS; i++) {
          const checkDate = format(subDays(new Date(), i + 1), "yyyy-MM-dd");
          const entry = entryMap.get(checkDate);
          if (entry && entry[column] !== null && entry[column] !== undefined) {
            lastLoggedDate = checkDate;
            break;
          }
        }

        // If no log found in lookback window, check if they have ANY log for this metric ever  
        if (!lastLoggedDate) {
          // Check a broader range
          const { data: oldEntry } = await supabase
            .from("daily_entries")
            .select(`entry_date, ${column}`)
            .eq("user_id", userId)
            .not(column, "is", null)
            .order("entry_date", { ascending: false })
            .limit(1);

          if (!oldEntry || oldEntry.length === 0) {
            // User has never logged this metric — skip it
            continue;
          }

          lastLoggedDate = oldEntry[0].entry_date;
        }

        // If last log was yesterday or today, no gap  
        if (lastLoggedDate === yesterday || lastLoggedDate === today) continue;

        // Calculate gap dates: from day after last log to yesterday
        const gapStart = subDays(new Date(), 
          differenceInDays(new Date(), parseISO(lastLoggedDate)) - 1
        );
        const gapDates: string[] = [];

        for (
          let d = gapStart;
          format(d, "yyyy-MM-dd") <= yesterday;
          d = new Date(d.getTime() + 86400000)
        ) {
          const dateStr = format(d, "yyyy-MM-dd");
          const entry = entryMap.get(dateStr);
          const hasValue = entry && entry[column] !== null && entry[column] !== undefined;
          const isMarked = noDataSet.has(`${dateStr}_${metricKey}`);

          if (!hasValue && !isMarked) {
            gapDates.push(dateStr);
          }
        }

        if (gapDates.length === 0) continue;

        // Cap at MAX_GAP_LOOKBACK_DAYS days of gaps
        const cappedDates = gapDates.slice(-MAX_GAP_LOOKBACK_DAYS);

        // Get label and emoji  
        const meta = STANDARD_METRICS[metricKey] || customMetricMap.get(metricKey) || {
          label: metricKey,
          emoji: "📊",
        };

        gaps.push({
          metricKey,
          metricLabel: meta.label,
          metricEmoji: meta.emoji,
          gapDates: cappedDates,
        });
      }

      if (gaps.length === 0) return null;

      return gaps;
    },
    enabled: !!userId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    // Only run once per session, don't refetch on window focus
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}
